import { useCallback, useContext, useState } from 'react';
import { LocaleContext, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { copyBlankConsentPdf } from '../../components/pts_document_modal/extract_blank_pdf';
import { getSavedChoiceSelections, getSavedDates, getSavedTexts, getSignedSignatureSummaries, } from '../../document_modal/document_modal.component';
import { buildConsentCopyPdfBytes, downloadBlob, printPdfBlob } from '../../file_upload';
import { isPtsDocument } from '../site_document_details.helpers';
const SIGNATURE_REASON_KEY = 'label.participant_documents.signature_reason';
const SIGNATURE_ACTION_KEYS = {
    credentials: 'label.participant_documents.signature_action.credentials',
    sso: 'label.participant_documents.signature_action.sso',
};
const SIGNER_ROLE_LABEL_KEYS = {
    'participant': 'label.participant_documents.signer_role.participant',
    'non-signing participant': 'label.participant_documents.signer_role.non_signing_participant',
    'third party': 'label.participant_documents.signer_role.third_party',
    'internal signer': 'label.participant_documents.signer_role.internal_signer',
};
const resolveKey = (translate, key) => {
    if (!key) {
        return undefined;
    }
    const resolved = translate(key);
    return resolved && resolved !== key ? resolved : undefined;
};
export const useDocumentPdfCopy = ({ document, enabled, isNoPiiSite = false, }) => {
    const translate = useLocalizeMessage();
    const appLocale = useContext(LocaleContext);
    const { addNotification } = useToastNotification();
    const [isCopyingPdf, setIsCopyingPdf] = useState(false);
    const localizedSignatureReasonRaw = translate(SIGNATURE_REASON_KEY);
    const localizedSignatureReason = localizedSignatureReasonRaw && localizedSignatureReasonRaw !== SIGNATURE_REASON_KEY
        ? localizedSignatureReasonRaw
        : undefined;
    const copyConsentPdf = useCallback(async (mode) => {
        if (!document || !enabled || isCopyingPdf) {
            return;
        }
        setIsCopyingPdf(true);
        try {
            if (isPtsDocument(document)) {
                await copyBlankConsentPdf({
                    blankPdfUrl: document.ec__document_template?.ec__pts_document?.url,
                    bundleUrl: document.ec__published_studio_template?.ec__bundle?.url,
                    mode,
                    fileName: `${document.ec__identifier || document._id}_consent.pdf`,
                });
                return;
            }
            const signatureLabels = {
                roles: Object.fromEntries(Object.entries(SIGNER_ROLE_LABEL_KEYS).map(([role, key]) => [role, resolveKey(translate, key)])),
                actions: Object.fromEntries(Object.entries(SIGNATURE_ACTION_KEYS).map(([method, key]) => [method, resolveKey(translate, key)])),
            };
            const summaries = getSignedSignatureSummaries(document, localizedSignatureReason, signatureLabels);
            const pdfBytes = await buildConsentCopyPdfBytes(document, summaries, appLocale, getSavedDates(document), isNoPiiSite ? [] : getSavedTexts(document), getSavedChoiceSelections(document));
            if (!pdfBytes) {
                addNotification({
                    title: '',
                    subtitle: translate('error.participant_documents.download_failed'),
                    type: 'error',
                });
                return;
            }
            const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
            const fileName = `${document.ec__identifier || document._id}_consent.pdf`;
            if (mode === 'download') {
                downloadBlob(blob, fileName);
            }
            else {
                printPdfBlob(blob);
            }
        }
        catch (error) {
            console.error('Failed to copy consent PDF:', error);
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.download_failed'),
                type: 'error',
            });
        }
        finally {
            setIsCopyingPdf(false);
        }
    }, [addNotification, appLocale, document, enabled, isCopyingPdf, isNoPiiSite, localizedSignatureReason, translate]);
    return { copyConsentPdf, isCopyingPdf };
};
